import { toast, TypeOptions } from "react-toastify";
import store from "@services/Redux";
import { Money } from "./currency";
import { Color, OrderStatus, Product, Size } from "./types";
import Api from "@services/Api";
import { toastAsHtmlElement } from "./toastAsHtml";
import env from "./env";

interface Item {
  product: Product;
  color: Color;
  sizes: {
    size: Size;
    quantity: number;
    price: number;
  }[];
}

// As many components can trigger error, and we are not sure if all of them throws 'Error'
// we use 'any' and then filter
export function toastError(error: any, type?: TypeOptions) {
  let message: string = "Erro desconhecido";
  if (error) {
    if (error.response?.data?.userMessage) message = error.response.data.userMessage;
    else if (error.response?.data?.message) message = error.response.data.message;
    else if (error.userMessage) message = error.userMessage;
    else if (error.message) message = error.message;
    else if (typeof error === "string") message = error;
  }
  error?.isHtml ? toastAsHtmlElement(message, type) : toast(message, { type: type ?? "error" });
}

export function toastMessage(message: string, type?: TypeOptions) {
  toast(message, { type });
}

export function getInt(float: number | string) {
  if (typeof float === "string") float = +float;
  let number = Math.round((float ?? 0) * Math.pow(10, 2));
  if (typeof number !== "number") return 0;
  return parseInt(`${number}`);
}

export function renameFile(originalFile: File, newName: string) {
  return new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
}

export function normalizeString(text: string) {
  if (!text) return "";
  text = text.toLowerCase();
  text = text.replace(new RegExp("[ÁÀÂÃ]", "gi"), "a");
  text = text.replace(new RegExp("[ÉÈÊ]", "gi"), "e");
  text = text.replace(new RegExp("[ÍÌÎ]", "gi"), "i");
  text = text.replace(new RegExp("[ÓÒÔÕ]", "gi"), "o");
  text = text.replace(new RegExp("[ÚÙÛ]", "gi"), "u");
  text = text.replace(new RegExp("[Ç]", "gi"), "c");
  text = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return text;
}

export function getSubdomain() {
  let subdomain = window.location.hostname.split(".")[0];
  if (subdomain === "localhost") subdomain = "";
  return subdomain;
}

export const uniqueBy = <T>(uniqueKey: keyof T, objects: T[]): T[] => {
  const ids = objects.map((object) => object[uniqueKey]);
  return objects.filter((object, index) => !ids.includes(object[uniqueKey], index + 1));
};

export const generateWhastappText = (customerName: string, sellerName?: string) => {
  const state = store.getState();
  const order = state.order.data;
  const itemsTotalValue = order?.items.reduce((acc, item) => Money(acc).add(Money(item.variation.price).multiply(item.quantity)).value, 0) ?? 0;

  const items = order?.items.reduce((cur, item) => {
    const productIndex = cur.findIndex((c) => c.product.id === item.product.id && c.color.id === item.variation.color.id);

    if (productIndex === -1) {
      cur.push({ product: item.product, color: item.variation.color, sizes: [{ size: item.variation.size, quantity: item.quantity, price: item.variation.price }] });
    } else {
      cur[productIndex].sizes?.push({ ...item.variation, quantity: item.quantity });
    }
    return cur;
  }, [] as Item[]);

  let text = `Olá${sellerName ? " " + sellerName : ""}, me chamo ${customerName} e acabei de fazer um pedido através do catálogo ${env.REACT_APP_PLATFORM_NAME}.`;
  text += `\n\n<--------------->\n\n`;
  text += items
    ?.map(
      (item) => `*•* ${item.product.ref} - *${item.product.name}* - ${item.color.name}
        ${item.sizes.map((size) => `*${size.size.name} - ${size.quantity}*`)}`
    )
    .join("\n");

  text += `\n\n<--------------->\n\n`;
  text += `Total de itens: *${Money(itemsTotalValue ?? 0).format()}*\n`;
  if (order?.discounts_value) text += `Descontos: *${Money(order?.discounts_value ?? 0).format()}*\n`;
  if (order?.shipment_value) text += `Frete: *${Money(order?.shipment_value ?? 0).format()}*\n`;
  if (order?.shipment_value || order?.discounts_value)
    text += `Total a pagar: *${Money(order.value ?? 0)
      .subtract(order.discounts_value ?? 0)
      .add(order.shipment_value ?? 0)
      .format()}*\n`;
  text += `\nLink do pedido: ${env.REACT_APP_BACKOFFICE_URL}/pedido/${order?.code}`;

  return encodeURIComponent(text);
};

export const setupSellerData = async (companyCode: string, sellerId: number | string) => {
  try {
    const { data } = await Api.get(`/user/seller/${sellerId}`);
    localStorage.setItem(`company_${companyCode}_seller_id`, `${sellerId}`);
    localStorage.setItem(`company_${companyCode}_seller_name`, `${data.seller?.name}`);
  } catch (error) {
    toastError(error);
  }
};

export const getStatusLabel = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.CREATED:
      return "Criado";
    case OrderStatus.WAITING_SELLER_CONFIRMATION:
      return "Em espera";
    case OrderStatus.READY_TO_PAY:
      return "Aguardando pagamento";
    case OrderStatus.WAITING_PAYMENT_CONFIRMATION:
      return "Aguardando confirmação de pagamento";
    case OrderStatus.IN_ANALYSIS:
      return "Em análise";
    case OrderStatus.PAID:
      return "Pago";
    case OrderStatus.CANCELLED:
      return "Cancelado";
    default:
      return "Sem status";
  }
};

export const getOrderStatusDescription = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.CREATED:
      return "Você pode fazer alterações no seu pedido.";
    case OrderStatus.WAITING_SELLER_CONFIRMATION:
      return "Seu pedido está aguardando a liberação do vendedor.";
    case OrderStatus.READY_TO_PAY:
      return "O vendedor confirmou o seu pedido, e você pode pagá-lo. Combine os detalhes de pagamento diretamente com o vendedor";
    case OrderStatus.WAITING_PAYMENT_CONFIRMATION:
      return "Seu pedido está aguardando confirmação de pagamento";
    case OrderStatus.IN_ANALYSIS:
      return "O seu pagamento está em análise. Este processo pode levar até 48 horas.";
    case OrderStatus.PAID:
      return "Seu pagamento foi confirmado, entre em contato com o vendedor para combinar a entrega.";
    case OrderStatus.CANCELLED:
      return "Cancelado";
    default:
      return "Sem status";
  }
};

export function enableSellerModeCommunication() {
  window.onmessage = function (e: MessageEvent) {
    try {
      if (!e.data?.token) return;
      localStorage.setItem("user_token", e.data?.token);
    } catch (error) {
      return;
    }
  };
  window.parent.postMessage("client_ready", "*");
}

export function isSellerModeOn() {
  if (localStorage.getItem(`seller_mode`)) {
    return true;
  } else {
    return false;
  }
}
